import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import request from '../../../services/Request';
import Swal from 'sweetalert2';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function ChangePasswordDialog({ open, onClose, userEmail, userId }) {
  const [tabValue, setTabValue] = useState(0);

  // Password form state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});

  // Email form state
  const [emailPassword, setEmailPassword] = useState('');
  const [email2, setEmail2] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailErrors, setEmailErrors] = useState({});
  const [loadingEmail, setLoadingEmail] = useState(false);

  // Fetch current email2 when dialog opens
  useEffect(() => {
    if (open && userId) {
      setLoadingEmail(true);
      request({
        method: 'get',
        url: `/users/edit/${userId}`,
      })
        .then((res) => {
          setEmail2(res.data.email2 || res.data.email || '');
        })
        .catch((err) => {
          console.error('Error fetching user data:', err);
        })
        .finally(() => {
          setLoadingEmail(false);
        });
    }
  }, [open, userId]);

  const resetForm = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordErrors({});
    setEmailPassword('');
    setEmailErrors({});
    setTabValue(0);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Password validation
  const validatePassword = () => {
    const newErrors = {};

    if (!oldPassword) {
      newErrors.oldPassword = 'Old password is required';
    } else if (oldPassword.length < 8) {
      newErrors.oldPassword = 'Password must be at least 8 characters';
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (confirmPassword !== newPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Email validation
  const validateEmail = () => {
    const newErrors = {};

    if (!emailPassword) {
      newErrors.password = 'Password is required';
    } else if (emailPassword.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!email2) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email2)) {
      newErrors.email = 'Please enter a valid email';
    }

    setEmailErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async () => {
    if (!validatePassword()) return;

    setPasswordLoading(true);
    try {
      await request({
        method: 'patch',
        url: '/users/edit/changePassword',
        data: {
          email: userEmail,
          oldPassword: oldPassword,
          newPassword: confirmPassword,
        },
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Your password has been changed',
      });

      handleClose();
    } catch (err) {
      if (err && err.response) {
        setPasswordErrors({ oldPassword: err.response.data.message });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to change password',
        });
      }
    }
    setPasswordLoading(false);
  };

  const handleEmailSubmit = async () => {
    if (!validateEmail()) return;

    setEmailLoading(true);
    try {
      await request({
        method: 'patch',
        url: '/users/edit/changeEmail',
        data: {
          email: userEmail,
          password: emailPassword,
          email2: email2,
        },
      });

      // Update localStorage with new email2
      const user = JSON.parse(localStorage.getItem('user'));
      if (user) {
        user.email2 = email2;
        localStorage.setItem('user', JSON.stringify(user));
      }

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Your associated email has been changed',
      });

      handleClose();
    } catch (err) {
      if (err && err.response) {
        setEmailErrors({ password: err.response.data.message });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to change email',
        });
      }
    }
    setEmailLoading(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Account Settings</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab icon={<LockOutlinedIcon />} label="Password" iconPosition="start" />
          <Tab icon={<EmailOutlinedIcon />} label="Email" iconPosition="start" />
        </Tabs>

        {/* Password Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Old Password"
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              error={!!passwordErrors.oldPassword}
              helperText={passwordErrors.oldPassword}
              autoFocus
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={!!passwordErrors.newPassword}
              helperText={passwordErrors.newPassword}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={!!passwordErrors.confirmPassword}
              helperText={passwordErrors.confirmPassword}
            />
            <Button
              variant="contained"
              onClick={handlePasswordSubmit}
              disabled={passwordLoading}
              fullWidth
              sx={{ mt: 1 }}
            >
              {passwordLoading ? 'Changing...' : 'Change Password'}
            </Button>
          </Box>
        </TabPanel>

        {/* Email Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              All notification emails will be sent to this associated email instead of your account email.
            </Typography>
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={emailPassword}
              onChange={(e) => setEmailPassword(e.target.value)}
              error={!!emailErrors.password}
              helperText={emailErrors.password}
            />
            <TextField
              fullWidth
              label="Associated Email"
              type="email"
              value={email2}
              onChange={(e) => setEmail2(e.target.value)}
              error={!!emailErrors.email}
              helperText={emailErrors.email}
              disabled={loadingEmail}
              placeholder={loadingEmail ? 'Loading...' : ''}
            />
            <Button
              variant="contained"
              onClick={handleEmailSubmit}
              disabled={emailLoading || loadingEmail}
              fullWidth
              sx={{ mt: 1 }}
            >
              {emailLoading ? 'Changing...' : 'Change Email'}
            </Button>
          </Box>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={passwordLoading || emailLoading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
